import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { MdCategory } from "react-icons/md";
import { LuSubtitles } from "react-icons/lu";
import { SiDialogflow } from "react-icons/si";
import { TbBrandAirtable, TbFileInvoice } from "react-icons/tb";
import { BiPurchaseTagAlt, BiSolidUserCheck } from "react-icons/bi";
import { FaAmazonPay, FaCcAmazonPay } from "react-icons/fa";

import Loader from "../Common/Loader/loader";
import DashboardCard from "./DashboardCard";
import { useSelector } from "react-redux";

const COLORS = {
  primary: "#2d6d96",
  success: "#2e7d32",
  info: "#0288d1",
  warning: "#ed6c02",
  teal: "#00796b",
  purple: "#6a1b9a",
  secondary: "#4f8fb5",
  brightPink: "#f50057",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { userResult } = useSelector((state) => state.Auth);
  console.log(userResult);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_PATH}/ebench/admin/get_dashboard_stats`
        );
        setStats(res?.data?.data || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cards = useMemo(
    () => [
      {
        title: "Today's Status",
        icon: BiSolidUserCheck,
        color: COLORS.brightPink,
        link: "#",
        total:
          (stats.todayRegisteredCandidates ?? 0) +
          (stats.todayProfileCompletedCandidates ?? 0),
        details: {
          "Candidate Registered": stats.todayRegisteredCandidates,
          "Candidate Profile Completed": stats.todayProfileCompletedCandidates,
          "Vendor Registered": stats.todayRegisteredVendors ?? 0,
        },
      },
      {
        title: "Vendors",
        icon: MdCategory,
        color: COLORS.primary,
        link: "/admin/vendor",
        total: (stats.activeVendors ?? 0) + (stats.inactiveVendors ?? 0),
        details: {
          Active: stats.activeVendors,
          Inactive: stats.inactiveVendors,
          Verified: stats.verifiedVendors,
          "Not Verified": stats.notVerifiedVendors,
        },
      },
      {
        title: "Candidates",
        icon: SiDialogflow,
        color: COLORS.info,
        link: "/admin/candidate",
        total: (stats.activeCandidates ?? 0) + (stats.inactiveCandidates ?? 0),
        details: {
          Active: stats.activeCandidates,
          Inactive: stats.inactiveCandidates,
          "Profile Completed": stats.profileCompletedCandidates,
          "Profile Partially Completed":
            stats.profilePartiallyCompletedCandidates,
          "Profile Incompleted": stats.profileIncompleteCandidates,
        },
      },
      {
        title: "Jobs",
        icon: LuSubtitles,
        color: COLORS.success,
        link: "/admin/job",
        total:
          (stats.postedJobs ?? 0) +
          (stats.draftedJobs ?? 0) +
          (stats.deletedJobs ?? 0) +
          (stats.expiredJobs ?? 0),
        details: {
          Posted: stats.postedJobs,
          Drafted: stats.draftedJobs,
          Deleted: stats.deletedJobs,
          Expired: stats.expiredJobs,
        },
      },
      {
        title: "Interviews",
        icon: TbBrandAirtable,
        color: COLORS.warning,
        link: "/admin/interview",
        total:
          (stats.scheduledInterviews ?? 0) +
          (stats.cancelledInterviews ?? 0) +
          (stats.completedInterviews ?? 0),
        details: {
          Scheduled: stats.scheduledInterviews,
          Cancelled: stats.cancelledInterviews,
          Completed: stats.completedInterviews,
        },
      },
      {
        title: "Purchase Orders",
        icon: BiPurchaseTagAlt,
        color: COLORS.teal,
        link: "/admin/purchase_order",
        total:
          (stats.candidatePurchaseOrders ?? 0) +
          (stats.supplierPurchaseOrders ?? 0) +
          (stats.clientPurchaseOrders ?? 0),
        details: {
          Candidate: stats.candidatePurchaseOrders,
          Supplier: stats.supplierPurchaseOrders,
          Client: stats.clientPurchaseOrders,
        },
      },

      {
        title: "Payments",
        icon: FaAmazonPay,
        color: COLORS.secondary,
        total: (stats.paidOrders ?? 0) + (stats.pendingOrders ?? 0),
        details: {
          Paid: stats.paidOrders,
          Pending: stats.pendingOrders,
          Cancelled: "-",
        },
      },
      {
        title: "Invoices",
        icon: TbFileInvoice,
        color: COLORS.purple,
        total: "-",
        details: {
          Pending: "-",
          Paid: "-",
          Overdue: "-",
        },
      },
      {
        title: "Revenue",
        icon: FaCcAmazonPay,
        color: COLORS.success,
        total: "-",
        details: {
          Today: "-",
          Monthly: "-",
          Growth: "-",
        },
      },
    ],
    [stats]
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
          color: "#fff",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Welcome {userResult?.firstName} 👋
        </Typography>
        <Typography sx={{ opacity: 0.9 }}>
          Real-time insights into your platform’s performance
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {cards.map((card, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
            <DashboardCard
              {...card}
              onClick={() => card.link && navigate(card.link)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
